/* (c) Quick Pro USA (quickprousa.com) - proprietary theme code. All rights reserved. */
/* qp-rime-event.js — click-to-reveal for sections/qp-rime-event.liquid when
   the `collapsed` setting is on ([data-qp-ev-collapse]). Adds .is-collapsible
   to the section root; every clipped/hidden state lives under that class, so
   no-JS (and the page before init) renders the band fully open.
   Closed: the clip's max-height = bottom of the top half (eyebrow, lockup,
   H2, plus the date slab when it sits beside them) + --qp-ev-peek; the lower
   content is inert. Open: max-height animates to scrollHeight, then `none`.
   Opens on the section's #anchor (load, hashchange, same-hash link clicks);
   starts open in the theme editor. Idempotent per node; re-inits on
   shopify:section:load. No dependencies. */
(function (win) {
  'use strict';
  var doc = win.document;
  var SEL = '[data-qp-ev-collapse]';
  var LOWER = '.qp-rime-event__text, .qp-rime-event__guest, .qp-rime-event__cta, ' +
    '.qp-rime-event__pending, .qp-rime-event__note, .qp-rime-event__details';

  function reduced() {
    return !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function setup(root) {
    if (root.__qpRimeEvent) return;
    var clip = root.querySelector('.qp-rime-event__clip');
    var bar = root.querySelector('.qp-rime-event__bar');
    var btn = bar && bar.querySelector('.qp-rime-event__toggle');
    if (!clip || !btn) return;
    root.__qpRimeEvent = true;

    var label = btn.querySelector('.qp-rime-event__toggle-label');
    var fade = root.querySelector('.qp-rime-event__fade');
    var slab = root.querySelector('[data-qp-ev-slab]');
    var heading = root.querySelector('.qp-rime-event__heading') ||
      root.querySelector('.qp-rime-event__lockup') ||
      root.querySelector('.qp-rime-event__eyebrow');
    var open = false;
    var timer = null;

    // Slab counts as "top half" only when it shares the heading's row (desktop).
    function slabBeside() {
      if (!slab || !heading) return false;
      return slab.getBoundingClientRect().top < heading.getBoundingClientRect().bottom;
    }

    function collapsedHeight() {
      var top = clip.getBoundingClientRect().top;
      var tops = root.querySelectorAll('.qp-rime-event__passed, .qp-rime-event__eyebrow, .qp-rime-event__lockup, .qp-rime-event__heading');
      var bottom = 0;
      Array.prototype.forEach.call(tops, function (el) {
        bottom = Math.max(bottom, el.getBoundingClientRect().bottom - top);
      });
      if (slabBeside()) bottom = Math.max(bottom, slab.getBoundingClientRect().bottom - top);
      var peek = parseFloat(win.getComputedStyle(root).getPropertyValue('--qp-ev-peek')) || 40;
      return Math.ceil(bottom + peek);
    }

    function setInert(on) {
      var nodes = Array.prototype.slice.call(root.querySelectorAll(LOWER));
      if (slab && !slabBeside()) nodes.push(slab);
      else if (slab) { slab.removeAttribute('inert'); slab.removeAttribute('aria-hidden'); }
      nodes.forEach(function (el) {
        if (on) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
        else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
      });
    }

    function slabAsButton(on) {
      if (!slab) return;
      if (on && slabBeside()) {
        slab.setAttribute('role', 'button');
        slab.setAttribute('tabindex', '0');
        slab.setAttribute('aria-controls', clip.id);
        slab.setAttribute('aria-expanded', 'false');
      } else {
        ['role', 'tabindex', 'aria-controls', 'aria-expanded'].forEach(function (a) { slab.removeAttribute(a); });
      }
    }

    function sync() {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (label) label.textContent = btn.getAttribute(open ? 'data-close-label' : 'data-open-label') || '';
      root.classList.toggle('is-open', open);
      setInert(!open);
      slabAsButton(!open);
    }

    function setOpen(next, instant) {
      if (next === open) return;
      open = next;
      clearTimeout(timer);
      var quick = instant || reduced();
      if (open) {
        clip.style.maxHeight = clip.scrollHeight + 'px';
        if (quick) clip.style.maxHeight = 'none';
        else timer = setTimeout(function () { if (open) clip.style.maxHeight = 'none'; }, 650);
      } else {
        clip.style.maxHeight = clip.scrollHeight + 'px';
        void clip.offsetHeight; // commit the start height before animating down
        clip.style.maxHeight = collapsedHeight() + 'px';
      }
      sync();
    }

    function refit() {
      if (!open) { clip.style.maxHeight = collapsedHeight() + 'px'; setInert(true); slabAsButton(true); }
    }

    function hashMatches() {
      return !!root.id && win.location.hash === '#' + root.id;
    }

    btn.addEventListener('click', function () {
      var wasOpen = open;
      setOpen(!open);
      if (wasOpen && root.getBoundingClientRect().top < 0) {
        root.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      }
    });
    if (fade) fade.addEventListener('click', function () { setOpen(true); });
    if (slab) {
      slab.addEventListener('click', function () { if (!open && slab.getAttribute('role') === 'button') setOpen(true); });
      slab.addEventListener('keydown', function (e) {
        if (open || slab.getAttribute('role') !== 'button') return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          setOpen(true);
          btn.focus({ preventScroll: true });
        }
      });
    }
    clip.addEventListener('transitionend', function (e) {
      if (e.target === clip && e.propertyName === 'max-height' && open) clip.style.maxHeight = 'none';
    });

    root.__qpRimeEventOpen = function () { setOpen(true); };
    root.__qpRimeEventHash = function () { if (hashMatches()) setOpen(true); };

    // Initial state: closed unless editor or the hash targets this band.
    root.classList.add('is-collapsible');
    bar.hidden = false;
    var startOpen = !!(win.Shopify && win.Shopify.designMode) || hashMatches();
    if (startOpen) {
      open = true;
      clip.style.maxHeight = 'none';
      sync();
    } else {
      open = false;
      clip.style.maxHeight = collapsedHeight() + 'px';
      sync();
    }

    if ('ResizeObserver' in win) {
      var ro = new win.ResizeObserver(function () { refit(); });
      var inner = root.querySelector('.qp-rime-event__inner');
      if (inner) ro.observe(inner);
    } else {
      var t;
      win.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(refit, 150); });
    }
  }

  function init(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var nodes = Array.prototype.slice.call(scope.querySelectorAll(SEL));
    if (scope.matches && scope.matches(SEL)) nodes.unshift(scope);
    nodes.forEach(function (root) {
      try { setup(root); } catch (err) {
        root.classList.remove('is-collapsible');
        var c = root.querySelector('.qp-rime-event__clip');
        if (c) c.style.maxHeight = '';
      }
    });
  }

  function each(fn) {
    Array.prototype.forEach.call(doc.querySelectorAll(SEL), function (root) {
      if (root[fn]) root[fn]();
    });
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', function () { init(doc); });
  } else {
    init(doc);
  }
  if (!doc.__qpRimeEventBound) {
    doc.__qpRimeEventBound = true;
    doc.addEventListener('shopify:section:load', function (e) { init(e.target || doc); });
    win.addEventListener('hashchange', function () { each('__qpRimeEventHash'); });
    // Same-hash link clicks don't fire hashchange; open the targeted band anyway.
    doc.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a[href*="#"]') : null;
      if (!a) return;
      var hash = a.getAttribute('href').split('#')[1];
      if (!hash) return;
      var root = doc.getElementById(hash);
      if (root && root.__qpRimeEventOpen && root.matches(SEL)) root.__qpRimeEventOpen();
    });
  }
})(window);
